import { canv } from "@/canv/global.js";
import {
    getColorProperty,
    getNumberProperty,
    getStringProperty,
    lightOrDark,
} from "@/canv/utils.js";
import { elementBase } from "@/elementBase/elementBase.js";
import type {
    AllowedBox,
    BorderData,
    ShapeStyles,
    ShapeWidgetFixReason,
    ShapeWidgetFixState,
    ShapeWidgetStateBorder,
    ShapeWidgetStateColor,
    ShapeWidgetStateFull,
    ShapeWidgetStateMap,
    ShapeWidgetStateMove,
    ShapeWidgetStateResize,
    ShapeWidgetStateShape,
    ShapeWidgetStateTextPart,
} from "@/elements/shape/types";
import { textWidget } from "@/elements/text/text.js";
import { widgetOption } from "@/widgetMenu/widgetMenu";

let DRAW_ERROR_LOGGED = false;

export class ShapeWidget extends elementBase {
    constructor(startX = 0, startY = 0, width = 0, height = 0) {
        super();

        this.startX = startX;
        this.startY = startY;
        this.width = width;
        this.height = height;

        this.textPart = new textWidget(
            this.startX + this.horizontalPadding,
            this.startY + this.topPadding,
        );

        this.textPart.shapeMode = true;

        this.textPart.menuTextAlign("center", true);
        this.textPart.setKeepMinWidth(false);

        if (lightOrDark(this.color) === "light") {
            this.textPart.menuTextColor("black", true);
        } else {
            this.textPart.menuTextColor("white", true);
        }

        this.updateDims();
    }

    updateDims(): void {
        this.horizontalPadding = 0.04 * this.width;
        this.verticalPadding = 0.04 * this.height;

        const shape = canv.shapeStorage.getShape(this.mode);

        if (shape.fitText !== undefined) {
            this.allowedBox = canv.shapeStorage
                .getShape(this.mode)
                .fitText(
                    this.textPart,
                    this.startX,
                    this.startY,
                    this.width,
                    this.height,
                    this.verticalPadding,
                    this.horizontalPadding,
                );
        }

        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    TryAction(type: string): void {
        this.textPart.TryAction(type);
        this.updateDims();

        if (type === "keyboardDown") {
            this.tryShortcut();
        }

        if (type === "mousedown" && !this.textPart.isOver()) {
            if (this.isOver()) {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("dragging");
            } else {
                canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
            }
        }
    }

    isInteraction(context: string, force = false): { type: string } {
        const shape = canv.shapeStorage.getShape(this.mode);

        if ((context === "focus" && this.isOver() && shape.includeText) || force) {
            this.fixState("textFocus");
            this.textPart.focusText();
            return {
                type: "interaction",
            };
        }

        if (context === "insertText" || context === "pastText") {
            this.fixState("textFocus");
            if (canv.keyboard.isEnter()) {
                this.textPart.focusTextByEnter();
            } else if (!canv.keyboard.isEnter()) {
                this.textPart.focusText();
                this.textPart.selection.selectAll();
                this.textPart.processDelete();
                this.TryAction("keyboardDown");
            }
            this.updateDims();
            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction(): void {
        canv.eventManager.openRecord();
        this.saveState("textFocus");
        canv.eventManager.closeRecord();

        this.textPart.unfocusText();
    }

    tryShortcut(): void {
        if (canv.keyboard.isEscCommand()) {
            // stopInteraction вызывается внутри cursorTool при изменении mode
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    clipArea(): void {
        const textCenterX = this.textPart.startX + this.textPart.width / 2;
        const textCenterY =
            this.textPart.startY - this.textPart.lines[0].textHeight + this.textPart.height / 2;

        canv.ctx.beginPath();
        canv.ctx.moveTo(
            textCenterX - this.allowedBox.allowedWidth / 2,
            textCenterY - this.allowedBox.allowedHeight / 2,
        );
        canv.ctx.lineTo(
            textCenterX + this.allowedBox.allowedWidth / 2,
            textCenterY - this.allowedBox.allowedHeight / 2,
        );
        canv.ctx.lineTo(
            textCenterX + this.allowedBox.allowedWidth / 2,
            textCenterY + this.allowedBox.allowedHeight / 2,
        );
        canv.ctx.lineTo(
            textCenterX - this.allowedBox.allowedWidth / 2,
            textCenterY + this.allowedBox.allowedHeight / 2,
        );
        canv.ctx.lineTo(
            textCenterX - this.allowedBox.allowedWidth / 2,
            textCenterY - this.allowedBox.allowedHeight / 2,
        );

        canv.ctx.clip();
    }

    draw(): void {
        const shape = canv.shapeStorage.getShape(this.mode);
        const shapeStyles: ShapeStyles = {
            color: this.color,
            borderColor: this.borderColor,
            borderDashType: this.borderDashType,
            borderWidth: this.borderWidth,
        };
        try {
            shape.draw(this.startX, this.startY, this.width, this.height, shapeStyles);
        } catch (e) {
            // в логах был некорректный arcTo. Скорее всего у фигуры rect.
            if (!DRAW_ERROR_LOGGED) {
                DRAW_ERROR_LOGGED = true;
                canv.captureException(e);
            }
            return;
        }

        if (shape.setDrawStyle) {
            shape.setDrawStyle?.(shapeStyles);
        }

        canv.ctx.save();

        if (
            !(
                canv.toolBox.TryGetToolByName("cursorTool").isMode("interaction") &&
                canv.canvasController.isInSelected(this.parent) &&
                canv.shapeStorage.getShape(this.mode).fitText !== undefined
            )
        ) {
            this.clipArea();
        }

        this.textPart.draw();

        canv.ctx.restore();

        canv.ctx.setLineDash([]);
    }

    translate(x: number, y: number): void {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    resize(x: number, y: number): void {
        const angle = Math.abs(this.parent.angle) % 360;
        const MIN_SIZE = 1;

        if (canv.keyboard.isShift()) {
            const delta = Math.max(x, y); // чтобы сохранять квадратность
            this.width += delta;
            this.height += delta;
        } else {
            if ((angle >= 70 && angle < 160) || (angle >= 250 && angle < 340)) {
                this.height += x;
                this.width += y;
            } else {
                this.height += y;
                this.width += x;
            }
        }
        if (this.width < MIN_SIZE) this.width = MIN_SIZE;
        if (this.height < MIN_SIZE) this.height = MIN_SIZE;

        this.updateDims();
    }

    clearText(): void {
        this.textPart.clearText();
        this.updateDims();
    }

    searchText(text: string): Array<{
        status: boolean;
        textWidget: textWidget;
        mode: string;
    }> {
        const shapeText = this.textPart.getText().toLowerCase();
        if (shapeText.includes(text)) {
            return [
                {
                    status: true,
                    textWidget: this.textPart,
                    mode: "widget",
                },
            ];
        }
        return [];
    }

    menuBorder(data: BorderData): void {
        const shapeTool = canv.toolBox.TryGetToolByName("shapeTool");
        const shape = canv.shapeStorage.getShape(this.mode);
        if (data.borderDashType !== undefined && shape.borderStyleEnabled) {
            this.borderDashType = data.borderDashType;
            shapeTool.setBorderDashType(this.borderDashType);
        } else if (data.borderColor !== undefined) {
            this.borderColor = data.borderColor;
            shapeTool.setBorderColor(this.borderColor);
        } else if (data.borderWidth !== undefined) {
            this.borderWidth = data.borderWidth;
            shapeTool.setBorderWidth(this.borderWidth);
        }
    }

    menuColor(color: string): void {
        this.color = color;
        if (lightOrDark(this.color) === "light") {
            this.textPart.menuTextColor("black", true);
        } else {
            this.textPart.menuTextColor("white", true);
        }
        const shapeTool = canv.toolBox.TryGetToolByName("shapeTool");
        shapeTool.setColor(this.color);
    }

    menuShape(shape: string): void {
        this.mode = shape;

        const shapeTool = canv.toolBox.TryGetToolByName("shapeTool");
        shapeTool.setMode(this.mode, true);

        if (this.isFocused) {
            this.textPart.setCursorToEnd();
        }

        this.updateDims();
    }

    menuTextSize(value: number): void {
        this.textPart.menuTextSize(value);
        this.updateDims();
    }

    menuTextBold(value: boolean): void {
        this.textPart.menuTextBold(value);
        this.updateDims();
    }

    menuTextStrike(value: boolean): void {
        this.textPart.menuTextStrike(value);
        this.updateDims();
    }

    menuTextBulleted(value: boolean): void {
        this.textPart.menuTextBulleted(value);
        this.updateDims();
    }

    menuTextAlign(value: string): void {
        this.textPart.menuTextAlign(value);
        this.updateDims();
    }

    menuLink(currentLink: string): void {
        if (this.textPart.isFocused) {
            this.textPart.menuLink(currentLink, this.updateDims.bind(this));
        } else {
            canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(this.parent);
            this.textPart.selection.selectAll();
            this.textPart.menuLink(currentLink, this.updateDims.bind(this));
        }
    }

    getWidgetOptions(): widgetOption[] {
        const widgetOptions: widgetOption[] = [];
        widgetOptions.push(new widgetOption("shape", this.mode, this.UID));

        if (canv.shapeStorage.getShape(this.mode).fillStyleEnabled) {
            widgetOptions.push(new widgetOption("color", this.color, this.UID));
        }

        const { borderStyleEnabled, borderWidthEnabled, borderColorEnabled, includeText } =
            canv.shapeStorage.getShape(this.mode);
        const data: BorderData = {
            borderColor: this.borderColor,
            borderDashType: this.borderDashType,
            borderWidth: this.borderWidth,
            borderStyleEnabled,
            borderWidthEnabled,
            borderColorEnabled,
        };

        widgetOptions.push(new widgetOption("border", data, this.UID));

        if (includeText) {
            widgetOptions.push(new widgetOption("textBold", this.textPart.fontBold, this.UID));
            widgetOptions.push(new widgetOption("textStrike", this.textPart.fontStrike, this.UID));
            widgetOptions.push(
                new widgetOption("textBulleted", this.textPart.bullettedList, this.UID),
            );
            widgetOptions.push(new widgetOption("textAlign", this.textPart.align, this.UID));
            widgetOptions.push(new widgetOption("textSize", this.textPart.fontSize, this.UID));

            this.textPart.setAffectedItemsUniqValues(widgetOptions, "link", "link", this.UID);
            this.textPart.setEditTextMobile(widgetOptions, this.UID);
        }

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        if (name === "shape") {
            return this.menuShape;
        } else if (name === "color") {
            return this.menuColor;
        } else if (name === "textBold") {
            return this.menuTextBold;
        } else if (name === "textStrike") {
            return this.menuTextStrike;
        } else if (name === "textBulleted") {
            return this.menuTextBulleted;
        } else if (name === "textSize") {
            return this.menuTextSize;
        } else if (name === "border") {
            return this.menuBorder;
        } else if (name === "link") {
            return this.menuLink;
        } else if (name === "mobileEdit") {
            return this.textPart.menuMobileEdit;
        } else if (name === "textAlign") {
            return this.menuTextAlign;
        }
    }

    getFixType(reason: ShapeWidgetFixReason): ShapeWidgetFixState {
        switch (reason) {
            case "move":
                return "move";
            case "resize":
                return "resize";
            case "wm_shape":
                return "shape";
            case "wm_color":
                return "color";
            case "wm_border":
                return "border";
            case "wm_textBold":
                return this.isFocused ? "full" : "bold";
            case "wm_textStrike":
                return this.isFocused ? "full" : "strike";
            case "wm_textBulleted":
                return this.isFocused ? "full" : "bulleted";
            case "wm_textSize":
                return this.isFocused ? "full" : "size";
            default:
                return "full";
        }
    }

    getState<T extends ShapeWidgetFixState>(fixType: T): ShapeWidgetStateMap[T] {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                } as ShapeWidgetStateMap[T];
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                } as ShapeWidgetStateMap[T];
            case "shape":
                return {
                    shape: this.mode,
                } as ShapeWidgetStateMap[T];
            case "color":
                return {
                    color: this.color,
                } as ShapeWidgetStateMap[T];
            case "border":
                return {
                    borderColor: this.borderColor,
                    borderDashType: this.borderDashType,
                    borderWidth: this.borderWidth,
                } as ShapeWidgetStateMap[T];
            case "bold":
            case "strike":
            case "bulleted":
            case "size":
                // TODO: убрать unknown при добавлении типизации textWidget
                return {
                    textPart: this.textPart.getState("full"),
                } as unknown as ShapeWidgetStateMap[T];
            case "full":
                // TODO: убрать unknown при добавлении типизации textWidget
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "shapeWidget",
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    color: this.color,
                    borderColor: this.borderColor,
                    borderDashType: this.borderDashType,
                    borderWidth: this.borderWidth,
                    mode: this.mode,
                    textPart: this.textPart.getState("full"),
                } as unknown as ShapeWidgetStateMap[T];
            default:
                return {} as never;
        }
    }

    loadState<T extends ShapeWidgetFixState>(stateObj: ShapeWidgetStateMap[T], fixType: T): void {
        switch (fixType) {
            case "move": {
                const state = stateObj as ShapeWidgetStateMove;
                this.startX = getNumberProperty("startX", state);
                this.startY = getNumberProperty("startY", state);
                break;
            }
            case "resize": {
                const state = stateObj as ShapeWidgetStateResize;
                this.startX = getNumberProperty("startX", state);
                this.startY = getNumberProperty("startY", state);
                this.width = getNumberProperty("width", state);
                this.height = getNumberProperty("height", state);
                break;
            }
            case "shape": {
                const state = stateObj as ShapeWidgetStateShape;
                this.mode = getStringProperty("shape", state);
                break;
            }
            case "color": {
                const state = stateObj as ShapeWidgetStateColor;
                this.color = getColorProperty("color", state);
                if (lightOrDark(this.color) === "light") {
                    this.textPart.menuTextColor("black", true);
                } else {
                    this.textPart.menuTextColor("white", true);
                }
                break;
            }
            case "border": {
                const state = stateObj as ShapeWidgetStateBorder;
                this.borderColor = getColorProperty("borderColor", state, "transparent");
                this.borderDashType = getStringProperty("borderDashType", state, "solid");
                this.borderWidth = getNumberProperty("borderWidth", state, 1);
                break;
            }
            case "bold":
            case "strike":
            case "bulleted":
            case "size": {
                const state = stateObj as ShapeWidgetStateTextPart;
                this.textPart.loadState(state.textPart, "full");
                break;
            }
            case "full": {
                const state = stateObj as ShapeWidgetStateFull;
                this.UID = getStringProperty("UID", state);
                this.parentUID = getStringProperty("parentUID", state);
                this.startX = getNumberProperty("startX", state);
                this.startY = getNumberProperty("startY", state);
                this.width = getNumberProperty("width", state);
                this.height = getNumberProperty("height", state);
                this.color = getColorProperty("color", state);
                this.borderColor = getColorProperty("borderColor", state, "transparent");
                this.borderDashType = getStringProperty("borderDashType", state, "solid");
                this.borderWidth = getNumberProperty("borderWidth", state, 1);
                this.mode = getStringProperty("mode", state);
                this.textPart.loadState(state.textPart, "full");

                // тут может прийти не правильный цвет текста (например, импорт miro). Для shape нам не важен цвет текста в state.
                // Мы сами его определяем по цвету shape и ставим menuTextColor
                if (lightOrDark(this.color) === "light") {
                    this.textPart.menuTextColor("black", true);
                } else {
                    this.textPart.menuTextColor("white", true);
                }
                break;
            }
        }

        // Именно два раза
        this.updateDims();
    }

    constructorName = "shape";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;

    textPart: textWidget;
    verticalPadding = 30;
    horizontalPadding = 30;
    topPadding = 30;

    color = "black";
    mode = "rect";
    isFocused = false;

    borderColor = "transparent";
    borderDashType = "solid";
    borderWidth = 1;
    allowedBox: AllowedBox = {
        allowedWidth: 0,
        allowedHeight: 0,
    };
}
